import { useState } from "react";
import { createSlug } from "../../utils/slug";
import { $enum } from "ts-enum-util";
import { Editor } from "@tinymce/tinymce-react";
import {
  Box,
  SimpleGrid,
  GridItem,
  Input,
  FormLabel,
  Button,
  useToast,
  Flex,
  IconButton,
  Select,
} from "@chakra-ui/react";
import React from "react";
import PageLayout from "../../components/ui/PageLayout";
import { Link } from "react-router-dom";
import {
  CreatePageMutationVariables,
  PageQuery,
  PageType,
  UpdatePageMutationVariables,
} from "../../generated/graphql";
import { FcList } from "react-icons/fc";

interface Props {
  handleCreatePage?: (data: CreatePageMutationVariables) => Promise<any>;
  handleUpdatePage?: (data: UpdatePageMutationVariables) => Promise<any>;
  id?: string;
  data?: PageQuery["page"];
  actionType: "CREATE" | "UPDATE";
}

const Form: React.FC<Props> = ({
  handleCreatePage,
  handleUpdatePage,
  id,
  data,
  actionType,
}) => {
  const toast = useToast();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [headline, setHeadline] = useState("");
  const [pageType, setPageType] = useState(data?.pageType || PageType.DEFAULT);
  const [showOnHeader, setShowOnHeader] = useState(data?.showOnHeader);
  const [showOnFooter, setShowOnFooter] = useState(data?.showOnFooter);
  const [status, setStatus] = useState(data?.status && true);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title && actionType === "CREATE") {
      return toast({
        status: "error",
        description: "Title is required",
        title: "Hata",
        duration: 1000,
      });
    }

    try {
      if (actionType === "CREATE") {
        await handleCreatePage!({
          data: {
            status,
            showOnFooter,
            showOnHeader,
            pageType,
            descriptions: {
              create: [
                {
                  title,
                  description,
                  slug: createSlug(title),
                  headline,
                },
              ],
            },
          },
        });
      }
      if (actionType === "UPDATE") {
        await handleUpdatePage!({
          where: {
            id: id,
          },
          data: {
            status: {
              set: status,
            },
            pageType: {
              set: pageType,
            },
            showOnHeader: {
              set: showOnHeader,
            },
            showOnFooter: {
              set: showOnFooter,
            },
          },
        });
      }
    } catch (error) {
      console.log("hata");
    }
  };

  return (
    <PageLayout>
      <Flex
        mb={6}
        justifyContent={"space-between"}
        alignItems="center"
        bgColor={"gray.100"}
        padding={2}
        rounded="md"
      >
        <Box fontSize={"18px"} fontWeight="bold">
          {actionType === "CREATE" ? "Create" : "Update"} Page
        </Box>
        <Flex experimental_spaceX={4}>
          <IconButton to={`/page`} as={Link} icon={<FcList />} aria-label="All Items" />
        </Flex>
      </Flex>
      <form onSubmit={handleSubmit}>
        <Box
          width={{
            base: "100%",
            md: "50%",
          }}
        >
          <SimpleGrid
            columns={2}
            gap={{
              base: 6,
              md: 6,
            }}
          >
            <GridItem
              colSpan={{
                base: 2,
                md: 2,
              }}
            >
              <FormLabel>Page Type</FormLabel>

              <Select
                value={pageType}
                onChange={(e) => setPageType(e.target.value as PageType)}
              >
                {$enum(PageType).map((a) => {
                  return (
                    <option value={a} key={a}>
                      {a}
                    </option>
                  );
                })}
              </Select>
            </GridItem>

            {actionType === "CREATE" && (
              <GridItem
                colSpan={{
                  base: 2,
                  md: 2,
                }}
              >
                <FormLabel>Title</FormLabel>

                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Title"
                />
              </GridItem>
            )}

            {actionType === "CREATE" && (
              <GridItem
                colSpan={{
                  base: 2,
                  md: 2,
                }}
              >
                <FormLabel>Headline</FormLabel>

                <Input
                  value={headline}
                  onChange={(e) => setHeadline(e.target.value)}
                  placeholder="Headline"
                />
              </GridItem>
            )}

            {actionType === "CREATE" && (
              <GridItem colSpan={2}>
                <FormLabel>Description</FormLabel>
                <Editor
                  apiKey="4vyocpjnhdhjtx3k08o1cv89jqhz5d6ae7910ubihafl7yes"
                  outputFormat="html"
                  onEditorChange={(e) => setDescription(e)}
                  init={{
                    skin: "snow",
                    icons: "thin",
                    placeholder: "Description about page",
                    height: 300,
                    menubar: false,
                    min_height: 200,
                    max_height: 500,

                    plugins: [
                      "advlist autolink lists link image charmap print preview anchor",
                      "searchreplace visualblocks code fullscreen",
                      "insertdatetime media table paste code wordcount",
                    ],
                    toolbar:
                      "undo redo | formatselect | " +
                      "bold italic backcolor forecolor | bullist numlist outdent indent | " +
                      "removeformat | code",
                  }}
                />
              </GridItem>
            )}

            <GridItem
              colSpan={{
                base: 2,
                md: 2,
              }}
            >
              <FormLabel>Show on Header</FormLabel>
              <SimpleGrid columns={2} gap={6}>
                <GridItem>
                  <Button
                    width={"full"}
                    colorScheme={showOnHeader ? "green" : "gray"}
                    onClick={() => setShowOnHeader(true)}
                  >
                    YES
                  </Button>
                </GridItem>

                <GridItem>
                  <Button
                    width={"full"}
                    colorScheme={!showOnHeader ? "red" : "gray"}
                    onClick={() => setShowOnHeader(false)}
                  >
                    NO
                  </Button>
                </GridItem>
              </SimpleGrid>
            </GridItem>

            <GridItem
              colSpan={{
                base: 2,
                md: 2,
              }}
            >
              <FormLabel>Show on Footer</FormLabel>
              <SimpleGrid columns={2} gap={6}>
                <GridItem>
                  <Button
                    width={"full"}
                    colorScheme={showOnFooter ? "green" : "gray"}
                    onClick={() => setShowOnFooter(true)}
                  >
                    YES
                  </Button>
                </GridItem>

                <GridItem>
                  <Button
                    width={"full"}
                    colorScheme={!showOnFooter ? "red" : "gray"}
                    onClick={() => setShowOnFooter(false)}
                  >
                    NO
                  </Button>
                </GridItem>
              </SimpleGrid>
            </GridItem>

            <GridItem
              colSpan={{
                base: 2,
                md: 2,
              }}
            >
              <FormLabel>Status</FormLabel>
              <SimpleGrid columns={2} gap={6}>
                <GridItem>
                  <Button
                    width={"full"}
                    colorScheme={status ? "green" : "gray"}
                    onClick={() => setStatus(true)}
                  >
                    ACTIVE
                  </Button>
                </GridItem>

                <GridItem>
                  <Button
                    width={"full"}
                    colorScheme={!status ? "red" : "gray"}
                    onClick={() => setStatus(false)}
                  >
                    PASSIVE
                  </Button>
                </GridItem>
              </SimpleGrid>
            </GridItem>
          </SimpleGrid>
          <Button colorScheme={"green"} type="submit" mt={6}>
            Submit
          </Button>
        </Box>
      </form>
    </PageLayout>
  );
};

export default Form;
